// /Users/shawn/💼_WORKSPACE/Projects/FixPriceAI-Desktop/src/App.js
// Purpose: Main desktop application component with drag & drop functionality
// NOT for: Mobile React Native components

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import SmartAIRouter from './services/SmartAIRouter';
import './App.css';

function App() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [userTier, setUserTier] = useState('free');
  const [location, setLocation] = useState('Warszawa');
  const [analysisStep, setAnalysisStep] = useState(0);

  const analysisSteps = [
    { icon: '🔍', text: 'Quick AI screening', completed: false },
    { icon: '🧠', text: 'Selecting best AI model', completed: false },
    { icon: '💰', text: 'Calculating costs', completed: false },
    { icon: '📊', text: 'Preparing estimate', completed: false },
    { icon: '📋', text: 'Building report', completed: false },
  ];

  const [steps, setSteps] = useState(analysisSteps);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage({
          file: file,
          preview: e.target.result,
          name: file.name
        });
        setAnalysisResult(null);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    multiple: false
  });

  const convertToBase64 = (dataURL) => {
    return dataURL.split(',')[1];
  };

  const analyzeImage = async () => {
    if (!selectedImage) return;

    setIsAnalyzing(true);
    setAnalysisResult(null);
    setSteps(analysisSteps.map(step => ({ ...step, completed: false })));
    setAnalysisStep(0);

    // Start step animation
    const stepInterval = setInterval(() => {
      setAnalysisStep(prev => {
        const next = prev + 1;
        setSteps(prevSteps =>
          prevSteps.map((step, index) => ({
            ...step,
            completed: index < next
          }))
        );

        if (next >= analysisSteps.length) {
          clearInterval(stepInterval);
          return prev;
        }
        return next;
      });
    }, 2500);

    try {
      const base64 = convertToBase64(selectedImage.preview);

      console.log('🖥️ Desktop: Starting analysis...');
      const result = await SmartAIRouter.analyzeConstructionProblem(
        base64,
        userTier,
        location
      );

      // Complete all steps
      setSteps(prevSteps =>
        prevSteps.map(step => ({ ...step, completed: true }))
      );

      setTimeout(() => {
        setAnalysisResult(result);
        setIsAnalyzing(false);
        clearInterval(stepInterval);
      }, 1000);

    } catch (error) {
      console.error('Analysis failed:', error);
      setIsAnalyzing(false);
      clearInterval(stepInterval);

      // Better error handling - set error state instead of alert
      setAnalysisResult({
        error: true,
        problem_type: "Błąd analizy",
        description: error.message || "Nie udało się przeanalizować zdjęcia. Sprawdź połączenie internetowe lub konfigurację API.",
        tips: [
          "Sprawdź jakość i oświetlenie zdjęcia",
          "Upewnij się że masz połączenie z internetem",
          "Sprawdź konfigurację kluczy API w pliku .env",
          "Aplikacja działa w trybie demo bez kluczy API"
        ]
      });
    }
  };

  const exportReport = () => {
    if (!analysisResult || !selectedImage) return;

    const reportData = {
      timestamp: new Date().toISOString(),
      image: selectedImage.name || 'unknown.jpg',
      location: location,
      userTier: userTier,
      analysis: analysisResult
    };

    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });

    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `FixPriceAI-Report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <h1>🏗️ FixPriceAI Desktop</h1>
            <p>Inteligentna wycena napraw budowlanych</p>
          </div>

          <div className="header-controls">
            <div className="tier-selector">
              <label>Plan:</label>
              <select
                value={userTier}
                onChange={(e) => setUserTier(e.target.value)}
                disabled={isAnalyzing}
              >
                <option value="free">Darmowy</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            <div className="location-selector">
              <label>Lokalizacja:</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={isAnalyzing}
              >
                <option value="Warszawa">Warszawa</option>
                <option value="Kraków">Kraków</option>
                <option value="Gdańsk">Gdańsk</option>
                <option value="Wrocław">Wrocław</option>
                <option value="Poznań">Poznań</option>
                <option value="Katowice">Katowice</option>
                <option value="Lublin">Lublin</option>
              </select>
            </div>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="main-grid">
          {/* Left Panel - Image Upload */}
          <div className="left-panel">
            <div className="upload-section">
              <h2>📸 Wgraj zdjęcie problemu</h2>

              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? 'active' : ''} ${selectedImage ? 'has-image' : ''}`}
              >
                <input {...getInputProps()} />

                {selectedImage ? (
                  <div className="image-preview">
                    <img src={selectedImage.preview} alt="Preview" />
                    <div className="image-overlay">
                      <p>📁 {selectedImage.name}</p>
                      <button
                        className="change-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(null);
                          setAnalysisResult(null);
                        }}
                      >
                        Zmień zdjęcie
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="dropzone-content">
                    <div className="dropzone-icon">📷</div>
                    <h3>Przeciągnij zdjęcie tutaj</h3>
                    <p>lub kliknij aby wybrać plik</p>
                    <small>Obsługiwane formaty: JPG, PNG, GIF, BMP, WebP</small>
                  </div>
                )}
              </div>

              {selectedImage && (
                <button
                  className="analyze-btn"
                  onClick={analyzeImage}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? '🔄 Analizowanie...' : '🧠 Analizuj AI'}
                </button>
              )}
            </div>

            {/* Analysis Progress */}
            {isAnalyzing && (
              <div className="analysis-progress">
                <h3>🤖 Multi-model AI w akcji</h3>
                <p className="progress-subtitle">
                  Używamy najlepszego modelu AI dla Twojego przypadku
                </p>

                <div className="steps-container">
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`step ${step.completed ? 'completed' : ''} ${index === analysisStep ? 'current' : ''}`}
                    >
                      <span className="step-icon">
                        {step.completed ? '✅' : index === analysisStep ? '⏳' : step.icon}
                      </span>
                      <span className="step-text">{step.text}</span>
                    </div>
                  ))}
                </div>

                <div className="analysis-time">
                  ⏱️ Multi-model analiza: 12-18 sekund
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Results */}
          <div className="right-panel">
            {analysisResult ? (
              <div className="results-section">
                <div className="results-header">
                  <h2>{analysisResult.error ? '❌ Błąd Analizy' : '📋 Szczegółowa Wycena'}</h2>
                  {!analysisResult.error && (
                    <>
                      <div className="ai-badge">
                        🧠 {analysisResult.ai_info?.model_used || 'AI'} • {userTier === 'premium' ? 'Premium' : 'Darmowy'}
                      </div>
                      <button className="export-btn" onClick={exportReport}>
                        💾 Eksportuj raport
                      </button>
                    </>
                  )}
                </div>

                <div className="results-content">
                  {analysisResult.error ? (
                    /* Error State */
                    <div className="result-card error-card">
                      <h3>❌ {analysisResult.problem_type}</h3>
                      <p>{analysisResult.description}</p>

                      <div className="result-card tips-card">
                        <h3>💡 Rozwiązania</h3>
                        <ul className="tips-list">
                          {analysisResult.tips?.map((tip, index) => (
                            <li key={index}>{tip}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="retry-section">
                        <button
                          className="analyze-btn"
                          onClick={() => {
                            setAnalysisResult(null);
                            if (selectedImage) analyzeImage();
                          }}
                        >
                          🔄 Spróbuj ponownie
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Problem Description */}
                      <div className="result-card problem-card">
                        <h3>🔍 Problem</h3>
                        <h4>{analysisResult.problem_type}</h4>
                        <p>{analysisResult.description}</p>
                        <div className="problem-meta">
                          <span className={`severity ${analysisResult.severity}`}>
                            {analysisResult.severity === 'low' ? '🟢 Niskie' :
                             analysisResult.severity === 'medium' ? '🟡 Średnie' : '🔴 Wysokie'} ryzyko
                          </span>
                          <span className={`urgency ${analysisResult.urgent ? 'urgent' : ''}`}>
                            {analysisResult.urgent ? '⚡ Pilne' : '📅 Standardowe'}
                          </span>
                        </div>
                      </div>

                  {/* Cost Breakdown */}
                  <div className="result-card cost-card">
                    <h3>💰 Koszty ({location})</h3>
                    <div className="cost-summary">
                      <div className="cost-range">
                        <span>Szacunkowy koszt:</span>
                        <strong>
                          {formatCurrency(analysisResult.estimated_cost.min)} - {formatCurrency(analysisResult.estimated_cost.max)}
                        </strong>
                      </div>
                      <div className="confidence">
                        Pewność: {analysisResult.estimated_cost.confidence === 'high' ? '🟢 Wysoka' :
                                   analysisResult.estimated_cost.confidence === 'medium' ? '🟡 Średnia' : '🟠 Niska'}
                      </div>
                    </div>

                    <div className="cost-breakdown">
                      <div className="breakdown-item">
                        <span>💼 Robocizna:</span>
                        <span>{formatCurrency(analysisResult.labor_cost)}</span>
                      </div>
                      {analysisResult.materials?.map((material, index) => (
                        <div key={index} className="breakdown-item">
                          <span>🔧 {material.name} ({material.quantity}):</span>
                          <span>{formatCurrency(material.cost)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="result-card timeline-card">
                    <h3>⏱️ Czas realizacji</h3>
                    <div className="timeline-info">
                      <span className="days">{analysisResult.timeline.days} dni</span>
                      <span className="description">{analysisResult.timeline.description}</span>
                    </div>
                  </div>

                  {/* Steps */}
                  <div className="result-card steps-card">
                    <h3>📝 Kroki wykonania</h3>
                    <ol className="steps-list">
                      {analysisResult.steps?.map((step, index) => (
                        <li key={index}>{step}</li>
                      ))}
                    </ol>
                  </div>

                  {/* Tips */}
                  <div className="result-card tips-card">
                    <h3>💡 Wskazówki</h3>
                    <ul className="tips-list">
                      {analysisResult.tips?.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </ul>
                  </div>

                  {/* AI Info */}
                  <div className="result-card ai-info-card">
                    <h3>🤖 Informacje o analizie</h3>
                    <div className="ai-details">
                      <div>Model AI: {analysisResult.ai_info?.model_used || 'N/A'}</div>
                      <div>Tier: {analysisResult.ai_info?.analysis_tier || userTier}</div>
                      <div>Czas analizy: {analysisResult.metadata?.processingTime || 'N/A'}ms</div>
                      <div>Lokalizacja: {location}</div>
                    </div>
                  </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-results">
                <div className="no-results-content">
                  <div className="no-results-icon">📊</div>
                  <h3>Wgraj zdjęcie aby rozpocząć</h3>
                  <p>
                    FixPriceAI Desktop analizuje zdjęcia problemów budowlanych
                    i dostarcza szczegółowe wyceny z polskimi cenami.
                  </p>
                  <div className="features-list">
                    <div className="feature">🧠 Multi-model AI analysis</div>
                    <div className="feature">💰 Regionalne ceny Polski</div>
                    <div className="feature">📋 Szczegółowe raporty</div>
                    <div className="feature">⚡ Inteligentna optymalizacja kosztów</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-left">
            <span>FixPriceAI Desktop v1.0.0</span>
            <span>•</span>
            <span>Powered by SmartAIRouter</span>
          </div>
          <div className="footer-right">
            <span>🇵🇱 Made for Polish construction market</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;